
import React, { useState } from 'react';
import { useSupplierLedger } from './hooks/useSuppliers';

const SupplierLedger: React.FC = () => {
  const { ledgerEntries, loading, error, suppliers } = useSupplierLedger();
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [transactionType, setTransactionType] = useState('');

  const filteredEntries = ledgerEntries?.filter(entry => 
    (selectedSupplier === '' || entry.supplier_id === selectedSupplier) &&
    (transactionType === '' || entry.transaction_type === transactionType) &&
    (dateRange.start === '' || new Date(entry.transaction_date) >= new Date(dateRange.start)) &&
    (dateRange.end === '' || new Date(entry.transaction_date) <= new Date(dateRange.end))
  );

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'order': return 'bg-blue-100 text-blue-800';
      case 'payment': return 'bg-green-100 text-green-800';
      case 'advance': return 'bg-yellow-100 text-yellow-800';
      case 'refund': return 'bg-purple-100 text-purple-800';
      case 'adjustment': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateSupplierBalance = (supplierId: string) => {
    const supplierEntries = ledgerEntries?.filter(entry => entry.supplier_id === supplierId) || [];
    return supplierEntries.reduce((balance, entry) => {
      return balance + (entry.credit_amount || 0) - (entry.debit_amount || 0);
    }, 0);
  };

  const supplierBalances = suppliers?.map(supplier => ({
    ...supplier,
    balance: calculateSupplierBalance(supplier.id)
  })) || [];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <p className="text-red-800">Error loading ledger: {error.message}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Supplier Ledger</h1>
        <div className="flex space-x-3">
          <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
            <i className="ri-download-line mr-2"></i>
            Export Ledger
          </button>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            <i className="ri-add-line mr-2"></i>
            Add Entry
          </button>
        </div>
      </div>

      {/* Supplier Balances Overview */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Supplier Balances</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {supplierBalances.slice(0, 6).map((supplier) => (
              <div key={supplier.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-medium text-gray-900 text-sm">{supplier.business_name}</h3>
                    <p className="text-xs text-gray-500">{supplier.contact_person_name}</p>
                  </div>
                  <button 
                    onClick={() => setSelectedSupplier(supplier.id)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <i className="ri-eye-line"></i>
                  </button>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">Balance:</span>
                  <span className={`font-bold ${supplier.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ₹{Math.abs(supplier.balance).toLocaleString()}
                    {supplier.balance < 0 ? ' (Dr)' : ' (Cr)'}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {supplierBalances.length > 6 && (
            <div className="mt-4 text-center">
              <button className="text-blue-600 hover:text-blue-800 text-sm">
                View All Suppliers ({supplierBalances.length})
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Supplier</label>
            <select
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Suppliers</option>
              {suppliers?.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.business_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Type</label>
            <select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Types</option>
              <option value="order">Order</option>
              <option value="payment">Payment</option>
              <option value="advance">Advance</option>
              <option value="refund">Refund</option>
              <option value="adjustment">Adjustment</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-end">
            <button 
              onClick={() => {
                setSelectedSupplier('');
                setTransactionType('');
                setDateRange({ start: '', end: '' });
              }}
              className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Ledger Entries */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Debit
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credit
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEntries?.map((entry) => {
                const supplier = suppliers?.find(s => s.id === entry.supplier_id);
                return (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {new Date(entry.transaction_date).toLocaleDateString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          {entry.reference_type}: {entry.reference_id?.slice(0, 8)}...
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {supplier?.business_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {supplier?.contact_person_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTransactionTypeColor(entry.transaction_type)}`}>
                        {entry.transaction_type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {entry.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-red-600">
                        {entry.debit_amount ? `₹${entry.debit_amount.toLocaleString()}` : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-medium text-green-600">
                        {entry.credit_amount ? `₹${entry.credit_amount.toLocaleString()}` : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className={`text-sm font-medium ${entry.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ₹{Math.abs(entry.balance).toLocaleString()}
                        {entry.balance < 0 ? ' (Dr)' : ' (Cr)'}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <i className="ri-money-dollar-circle-line text-blue-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                ₹{(ledgerEntries?.reduce((sum, entry) => sum + (entry.debit_amount || 0), 0) || 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Total Debits</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <i className="ri-money-dollar-circle-line text-green-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                ₹{(ledgerEntries?.reduce((sum, entry) => sum + (entry.credit_amount || 0), 0) || 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Total Credits</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <i className="ri-user-line text-yellow-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {supplierBalances.filter(s => s.balance > 0).length}
              </div>
              <div className="text-sm text-gray-500">Credit Balances</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <i className="ri-user-line text-red-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {supplierBalances.filter(s => s.balance < 0).length}
              </div>
              <div className="text-sm text-gray-500">Debit Balances</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierLedger;
